import type { RouteObject } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { canAccessAnalytics } from "@/components/ProjectSidebar/permissions";
import { RequireAuth } from "@/components/Routing/RequireAuth";
import { dashboardStartupContext, shouldRevalidateOnPathChange } from "@/providers/dashboardStartup";

import { analyticsRoutes } from "./routes";

export const analyticsRoute = [
  {
    path: analyticsRoutes.overview,
    // Start the overview cards' queries alongside the session user.
    loader: ({ context, params }) => {
      const startup = context.get(dashboardStartupContext);
      if (startup) {
        import("./pages/overview/analyticsOverviewQueries")
          .then(({ prefetchAnalyticsOverviewRoute }) => prefetchAnalyticsOverviewRoute(startup, params.slug))
          .catch(() => {});
      }

      return null;
    },
    shouldRevalidate: shouldRevalidateOnPathChange,
    lazy: async () => {
      const { AnalyticsOverviewPage } = await import("./pages/overview/AnalyticsOverviewPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <AnalyticsOverviewPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.adoptionDetails,
    lazy: async () => {
      const { AdoptionDetailPage } = await import("./pages/details/adoption/AdoptionDetailPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <AdoptionDetailPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.contributionDetails,
    lazy: async () => {
      const { ContributionDetailPage } = await import("./pages/details/contribution/ContributionDetailPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <ContributionDetailPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.engagementDetails,
    lazy: async () => {
      const { EngagementDetailPage } = await import("./pages/details/engagement/EngagementDetailPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <EngagementDetailPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.bookingReporting,
    lazy: async () => {
      const { BookingAnalyticsPage } = await import("./pages/bookings/BookingAnalyticsPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <BookingAnalyticsPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.communityFeedReporting,
    lazy: async () => {
      const { CommunityFeedAnalyticsPage } = await import("./pages/community-feed/CommunityFeedAnalyticsPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <CommunityFeedAnalyticsPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.communityFeedSentimentReporting,
    lazy: async () => {
      const { CommunityFeedSentimentAnalyticsPage } =
        await import("./pages/community-feed-sentiment/CommunityFeedSentimentAnalyticsPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <CommunityFeedSentimentAnalyticsPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.eventReporting,
    lazy: async () => {
      const { EventAnalyticsPage } = await import("./pages/events/EventAnalyticsPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <EventAnalyticsPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.ticketReporting,
    lazy: async () => {
      const { TicketAnalyticsPage } = await import("./pages/tickets/TicketAnalyticsPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <TicketAnalyticsPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.explore,
    lazy: async () => {
      const { AnalyticsExplorePage } = await import("./pages/explore/AnalyticsExplorePage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <AnalyticsExplorePage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.exploreEvents,
    lazy: async () => {
      const { EventAnalyticsPage } = await import("./pages/events/EventAnalyticsPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <EventAnalyticsPage mode="all" />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.exploreAdoption,
    lazy: async () => {
      const { AdoptionDetailPage } = await import("./pages/details/adoption/AdoptionDetailPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <AdoptionDetailPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.exploreEngagement,
    lazy: async () => {
      const { EngagementDetailPage } = await import("./pages/details/engagement/EngagementDetailPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <EngagementDetailPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.exploreContribution,
    lazy: async () => {
      const { ContributionDetailPage } = await import("./pages/details/contribution/ContributionDetailPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <ContributionDetailPage />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.exploreBookings,
    lazy: async () => {
      const { BookingAnalyticsPage } = await import("./pages/bookings/BookingAnalyticsPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <BookingAnalyticsPage mode="all" />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.exploreCommunityFeed,
    lazy: async () => {
      const { CommunityFeedAnalyticsPage } = await import("./pages/community-feed/CommunityFeedAnalyticsPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <CommunityFeedAnalyticsPage mode="all" />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.exploreSentiment,
    lazy: async () => {
      const { CommunityFeedSentimentAnalyticsPage } =
        await import("./pages/community-feed-sentiment/CommunityFeedSentimentAnalyticsPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <CommunityFeedSentimentAnalyticsPage mode="all" />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: analyticsRoutes.exploreTickets,
    lazy: async () => {
      const { TicketAnalyticsPage } = await import("./pages/tickets/TicketAnalyticsPage");

      return {
        element: (
          <RequireAuth permission={canAccessAnalytics}>
            <TicketAnalyticsPage mode="all" />
          </RequireAuth>
        ),
      };
    },
  },
  {
    path: "*",
    element: <ErrorPage status={404} />,
  },
] satisfies RouteObject[];
